import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Channel } from './Channel'
import { Customizable } from './Customizable'
import { Field } from './Field'
import { Location } from './Location'
import { RecordDataMap } from './RecordDataMap'
import { RecordFactory } from './RecordFactory'
import { FKType } from './SQLBuilder'
import { User } from './User'

type ClientConstructorArgs = Omit<ConstructorArgs<() => Client>, 'birth_day'> & {
    birthday: moment.Moment | number | string | null
}

export class Client extends BaseRecord implements Customizable {
    readonly recordType = 'client'

    readonly iCustomizable = true

    cost?: number
    birthDay?: moment.Moment
    lockedOutDate?: moment.Moment
    cancelledDate?: moment.Moment
    lastSourceDate?: moment.Moment
    lastSourcePOSDate?: moment.Moment
    lastPaymentDate?: moment.Moment
    firstPaymentDate?: moment.Moment
    lastTransactionDate?: moment.Moment
    firstTransactionDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    fieldValues: RecordDataMap
    discount?: number
    discountType?: string
    notify?: string
    reminder?: string
    allowTrigger?: boolean

    constructor(data?: Partial<ClientConstructorArgs>, params?: { person?: User }) {
        super(data)
        if (!data) {
            this.fieldValues = new RecordDataMap()
            this.person = params?.person
            this.creator = PSApplication.me()
            this.employee = this.creator.employee
            this.owner = this.creator.owner
            return
        }

        this.cost = this.propertyFromCurrencyArg(data.cost)
        this.lockedOutDate = this.propertyFromTimestampArg(data.locked_out_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.lastPaymentDate = this.propertyFromTimestampArg(data.last_payment_date)
        this.firstPaymentDate = this.propertyFromTimestampArg(data.first_payment_date)
        this.lastTransactionDate = this.propertyFromTimestampArg(data.last_transaction_date)
        this.firstTransactionDate = this.propertyFromTimestampArg(data.first_transaction_date)
        this.lastSourceDate = this.propertyFromTimestampArg(data.last_source_date)
        this.lastSourcePOSDate = this.propertyFromTimestampArg(data.last_source_pos_date)
        this.discountType = data.discount_type ?? undefined
        this.discount = this.propertyFromCurrencyArg(data.discount)
        this.notify = data.notify ?? undefined
        this.reminder = data.reminder ?? undefined
        this.birthDay = this.propertyFromDateArg(data.birthday)
        this.allowTrigger = data.allow_trigger ?? false
        this.fieldValues = Field.loadFromRecordMap(
            new RecordDataMap(data),
            data.owner ? (data.owner instanceof User ? data.owner.id : data.owner) : undefined
        )
    }

    clone(overrides?: Partial<ClientConstructorArgs>): Client {
        return new Client({ ...this.serializeForClone(), birthday: this.birthDay, ...overrides })
    }

    get notifyType(): string | undefined {
        return this.notify
    }

    static initWithPerson(person: User): Client {
        return new Client(undefined, { person })
    }

    async fields(): Promise<Field[]> {
        return await RecordFactory.getInstance().loadClientFields(
            this.owner!.id!,
            PSApplication.role() === User.PERM_ROLE_CLIENT
        )
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.employee) this.employee = BaseRecord.fromSerializer(data.employee, User)
        if (data.location) this.location = BaseRecord.fromSerializer(data.location, Location)
        if (data.referer) this.referer = BaseRecord.fromSerializer(data.referer, User)
        if (data.channel) this.channel = BaseRecord.fromSerializer(data.channel, Channel)
        if (data.birthDay) this.birthDay = moment(data.birthDay)
        if (data.lockedOutDate) this.lockedOutDate = moment(data.lockedOutDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastSourceDate) this.lastSourceDate = moment(data.lastSourceDate)
        if (data.lastSourcePOSDate) this.lastSourcePOSDate = moment(data.lastSourcePOSDate)
        if (data.lastPaymentDate) this.lastPaymentDate = moment(data.lastPaymentDate)
        if (data.firstPaymentDate) this.firstPaymentDate = moment(data.firstPaymentDate)
        if (data.lastTransactionDate) this.lastTransactionDate = moment(data.lastTransactionDate)
        if (data.firstTransactionDate) this.firstTransactionDate = moment(data.firstTransactionDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.fieldValues) this.fieldValues = new RecordDataMap(data.fieldValues)
    }

    static fk: FKType = {
        users: ['person', 'owner', 'creator', 'employee', 'referer'],
        channels: ['channel'],
    }

    static dataType(): DataType {
        return 'clients'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['person'] = this.person?.id ?? ''
        dict['employee'] = this.employee?.id ?? ''
        dict['location'] = this.location?.id ?? ''
        dict['referer'] = this.referer?.id ?? ''
        dict['channel'] = this.channel?.id ?? ''
        dict['cost'] = this.cost ?? ''
        dict['discount'] = this.discount
        dict['allow_trigger'] = this.allowTrigger
        dict['birthday'] = this.birthDay?.format('YYYY-MM-DD')
        dict['discount_type'] = this.discountType ?? ''
        dict['notify'] = this.notify ?? ''
        dict['reminder'] = this.reminder ?? ''
        if (this.fieldValues)
            dict['field_values'] = Field.serializeFieldData(
                this.fieldValues,
                this.owner?.id,
                undefined
            )
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get employee(): User | undefined {
        return this.getForeignRecord('employee', User)
    }
    set employee(employee: User | undefined) {
        this.setForeignRecord('employee', employee)
    }
    get location(): Location | undefined {
        return this.getForeignRecord('location', Location)
    }
    set location(location: Location | undefined) {
        this.setForeignRecord('location', location)
    }
    get referer(): User | undefined {
        return this.getForeignRecord('referer', User)
    }
    set referer(referer: User | undefined) {
        this.setForeignRecord('referer', referer)
    }
    get channel(): Channel | undefined {
        return this.getForeignRecord('channel', Channel)
    }
    set channel(channel: Channel | undefined) {
        this.setForeignRecord('channel', channel)
    }
}
